import React, { useState, useCallback } from "react";
import { Dialog, Grid, Button, IconButton, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "@mui/styles";

import { ButtonAddressSearch, DialogTitle, DialogContent } from "../";
import AddressBookService from "../../services/address_book";
import AddressSearch from "../AddressSearch";
import { convertPhone } from "../../lib";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

const useStyles = makeStyles(() => ({
  buttonClose: {
    position: "absolute",
    top: 22,
    right: 30,
  },
  listItem: {
    width: 350,
  },
  addressBase: {
    width: 253,
  },
  buttonAddressSearch: {
    marginLeft: 12,
  },
  buttonRegistration: {
    width: 168,
    height: 36,
    marginBottom: 18,
  },
}));

type FormData = {
  name: string;
  phone: string;
  addressBase: string;
  addressDetail: string;
  placeInfoForCustomer: string;
};

export default function Registration(props: any) {
  const classes = useStyles();
  const { address, refresh, close } = props;

  const navigate = useNavigate();
  const { register, watch, setValue } = useForm<FormData>({
    defaultValues: {
      name: address ? address.name : "",
      phone: address ? address.phone : "",
      addressBase: address ? address.addressBase : "",
      addressDetail: address ? address.addressDetail : "",
      placeInfoForCustomer: address ? address.placeInfoForCustomer : "",
    }
  });

  const [showSearchAddressPage, setShowSearchAddressPage] =
    useState<boolean>(false);

  const setAddress = useCallback(
    (base: string) => {
      setValue('addressBase', base);
      setValue('addressDetail', "");
    },
    [setValue]
  );

  const validate = (data: any): boolean => {
    if (data.name.length < 1) {
      alert("장소명을 입력해주세요.");
      return false;
    }

    if (data.phone.length < 4) {
      alert("연락처를 입력해주세요.");
      return false;
    }

    if (data.addressBase.length < 4) {
      alert("주소를 입력해주세요.");
      return false;
    }

    return true;
  };

  const registerAccount = useCallback(async () => {
    try {
      const data = watch();
      if (!validate(data)) return; //폼 유효성 검증 실패시 리턴

      const addressBookService = new AddressBookService(navigate);
      if (address === null) {
        await addressBookService.register(data);
      } else {
        await addressBookService.modify(address.id, data);
      }

      alert("주소가 저장되었습니다.");
      close();
      await refresh();
    } catch (e: any) {
      if (e.response !== undefined) {
        if (e.response.data.code === "E001") {
          alert(e.response.data.message);
        }
        return;
      }
    }
  }, [watch, navigate, address, close, refresh]);

  return (
    <Dialog
      disableEscapeKeyDown
      maxWidth={"lg"}
      aria-labelledby="confirmation-dialog-title"
      open={true}
      onClose={() => close()}
    >
      <IconButton size="small" onClick={close} className={classes.buttonClose}>
        <CloseIcon />
      </IconButton>
      <DialogTitle>주소 등록/수정</DialogTitle>
      <DialogContent>
        <AddressSearch
          open={showSearchAddressPage}
          close={(isSelectedAddress: boolean) =>
            setShowSearchAddressPage(false)
          }
          setAddress={setAddress}
          setAddressBaseJibun={(jibun: string) => jibun}
        />
        <form>
          <Grid
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
            spacing={3}
          >
            <Grid item className={classes.listItem}>
              <TextField
                {...register("name")}
                id="장소명"
                fullWidth
                required
                label="장소명"
                variant="standard"
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{ maxLength: 12 }}
                placeholder={"ex) 생산공장1"}
                defaultValue={address && address.name}
              />
            </Grid>
            <Grid item className={classes.listItem}>
              <TextField
                {...register("phone")}
                id="phone"
                fullWidth
                required
                variant="standard"
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{ maxLength: 13 }}
                label="연락처"
                placeholder={"ex) 010-1111-2222"}
                defaultValue={address && address.phone}
                onChange={(e: any) => {
                  e.target.value = convertPhone(e.target.value);
                }}
              />
            </Grid>
            <Grid item className={classes.listItem}>
              <TextField
                {...register("addressBase")}
                id="addressBase"
                fullWidth
                required
                variant="standard"
                className={classes.addressBase}
                InputLabelProps={{
                  shrink: true,
                }}
                label="주소"
                // disabled : readonly 를 위해 이 옵션을 주면 handleSubmit에 데이터가 안 담김. InputProps로 해결
                InputProps={{
                  readOnly: true,
                }}
                onClick={() => setShowSearchAddressPage(true)}
              />
              <ButtonAddressSearch
                className={classes.buttonAddressSearch}
                onClick={() => setShowSearchAddressPage(true)}
              />
            </Grid>
            <Grid item className={classes.listItem}>
              <TextField
                {...register("addressDetail")}
                id="addressDetail"
                fullWidth
                required
                variant="standard"
                inputProps={{ maxLength: 40 }}
                label="주소 상세"
                placeholder={"상세주소를 입력해주세요"}
              />
            </Grid>
            <Grid item className={classes.listItem}>
              <TextField
                {...register("placeInfoForCustomer")}
                id="placeInfoForCustomer"
                fullWidth
                variant="standard"
                inputProps={{ maxLength: 100 }}
                label="장소 정보"
                placeholder={"장소의 특이사항(제한길이 등)을 입력해주세요"}
              />
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                size={"small"}
                className={classes.buttonRegistration}
                onClick={registerAccount}
              >
                저장
              </Button>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
}
